import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, Circle, CheckCircle2, Utensils, ArrowRight, Plus, X, Pencil, Check, Trash2, GripVertical, ShoppingBasket,
} from "lucide-react";
import {
  DndContext, MouseSensor, TouchSensor, useSensor, useSensors,
  useDraggable, useDroppable, DragOverlay, closestCenter,
} from "@dnd-kit/core";
import { useAuth } from "@/context/AuthContext";
import { plannerAPI, sundayOf, ymd, addDays, formatTime, recipePhotoUrl } from "@/lib/planner";
import { toast } from "sonner";
import MealPicker from "@/components/planner/MealPicker";
import CopyWeekDialog from "@/components/planner/CopyWeekDialog";
import MealTypeTabs from "@/components/planner/MealTypeTabs";
import PlannerOnboarding from "@/components/planner/PlannerOnboarding";
import BotanicalSprig from "@/components/planner/BotanicalSprig";
import { EventDialog } from "@/pages/planner/PlannerCalendar";

/**
 * PlannerHome — the /planner/home dashboard.
 *
 * Layout (matches the mockup):
 *   Row 1: TODAY (60%) | THIS WEEK (40%)
 *          "Things I'm Carrying" sits under TODAY on the left column.
 *   Row 2: MEAL PLAN (60%) | Quote card (40%)
 *   Row 3: "Goals & Home" — user-authored personal goals, up to 4 tiles.
 *
 * "Things I'm Carrying" and "Goals & Home" are localStorage-backed for
 * now so a user can start using them immediately without shipping a new
 * backend schema; we'll migrate to a real endpoint once the shape settles.
 */
export default function PlannerHome() {
  const { user } = useAuth();
  const [today, setToday] = useState({ events: [], date: ymd(new Date()) });
  const [week, setWeek] = useState({ events: [], by_day: {}, start: ymd(sundayOf(new Date())) });
  const [meals, setMeals] = useState({ entries: [] });
  const [loading, setLoading] = useState(true);
  // Inline meal picker — opens against a specific date and refreshes the
  // dashboard's meal-plan card after saving, so you never leave /planner/home.
  const [picker, setPicker] = useState({ open: false, date: null, replaceEntryId: null });
  const [copyWeekOpen, setCopyWeekOpen] = useState(false);
  const [mealType, setMealType] = useState("dinner");
  const [editingEvent, setEditingEvent] = useState(null);

  const loadMeals = async () => {
    try {
      const weekStart = ymd(sundayOf(new Date()));
      const m = await plannerAPI.meals(weekStart);
      setMeals(m);
    } catch { /* toast handled elsewhere on first load */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const weekStart = ymd(sundayOf(new Date()));
        const [t, w, m] = await Promise.all([
          plannerAPI.today(),
          plannerAPI.week(weekStart),
          plannerAPI.meals(weekStart),
        ]);
        setToday(t);
        setWeek(w);
        setMeals(m);
      } catch {
        toast.error("Couldn't load your Planner. Try again in a minute.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const firstName = (user?.name || user?.username || "").split(/[\s.]/)[0] || "there";
  const weekStart = new Date(week.start + "T00:00:00");
  const weekEnd = addDays(weekStart, 6);
  const weekLabel = `${weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${weekEnd.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

  const toggleDone = async (ev) => {
    try {
      const updated = await plannerAPI.updateEvent(ev.id, { done: !ev.done_at });
      setToday({ ...today, events: today.events.map((e) => (e.id === ev.id ? updated : e)) });
    } catch {
      toast.error("Couldn't update that.");
    }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await plannerAPI.deleteEvent(ev.id);
      setToday((prev) => ({ ...prev, events: prev.events.filter((e) => e.id !== ev.id) }));
      toast.success("Deleted.");
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  const refreshToday = async () => {
    try {
      const t = await plannerAPI.today();
      setToday(t);
    } catch { /* ignore */ }
  };

  const removeMeal = async (entryId) => {
    try {
      await plannerAPI.deleteMeal(entryId);
      await loadMeals();
    } catch {
      toast.error("Couldn't remove that meal.");
    }
  };

  // ---------- Drag-to-swap wiring ----------
  // MouseSensor + TouchSensor (rather than the unified PointerSensor) so
  // both real users and Playwright's mouse.* API drive drags reliably; the
  // 6px distance keeps taps inside a draggable cell working as clicks.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );
  const [dragging, setDragging] = useState(null); // { date, title, photo }
  const handleDragStart = (e) => {
    const src = visibleMealEntries.find((m) => m.date === e.active.id);
    if (!src) return;
    setDragging({
      date: e.active.id,
      title: src.recipe?.title || "Recipe",
      photo: src.recipe?.photo_path || null,
    });
  };
  const handleDragEnd = async (e) => {
    setDragging(null);
    const from = e.active?.id;
    const to = e.over?.id;
    if (!from || !to || from === to) return;

    // Optimistic swap — scoped to the currently visible meal_type only.
    const prev = meals.entries || [];
    const next = prev.map((m) => {
      const mt = m.meal_type || "dinner";
      if (mt !== mealType) return m;
      if (m.date === from) return { ...m, date: to };
      if (m.date === to) return { ...m, date: from };
      return m;
    });
    setMeals((cur) => ({ ...cur, entries: next }));

    try {
      await plannerAPI.swapMeals(from, to, mealType);
      await loadMeals();
    } catch {
      toast.error("Couldn't swap those meals.");
      await loadMeals();
    }
  };

  // Meals for the currently-active tab + per-tab counts for the switcher badge.
  const allMealEntries = meals.entries || [];
  const mealCounts = useMemo(() => {
    const c = { breakfast: 0, lunch: 0, dinner: 0 };
    for (const e of allMealEntries) c[e.meal_type || "dinner"] += 1;
    return c;
  }, [allMealEntries]);
  const visibleMealEntries = useMemo(
    () => allMealEntries.filter((e) => (e.meal_type || "dinner") === mealType),
    [allMealEntries, mealType],
  );

  return (
    <div className="space-y-8 relative" data-testid="planner-home">
      <PlannerOnboarding />

      {/* Page-level botanical corner accents — soft camel so they read as
          watercolor sprigs behind the layout, not emojis. */}
      <BotanicalSprig
        variant="branch"
        color="#A87F56"
        opacity={0.16}
        size={260}
        className="pointer-events-none hidden lg:block absolute -top-10 -right-6 z-0"
      />
      <BotanicalSprig
        variant="twig"
        color="#A87F56"
        opacity={0.14}
        size={220}
        flip
        className="pointer-events-none hidden lg:block absolute -bottom-6 -left-8 z-0"
      />

      {/* Greeting */}
      <div className="relative flex items-start gap-3 sm:gap-4">
        <BotanicalSprig
          variant="twig"
          color="#3E4A37"
          opacity={0.45}
          size={70}
          rotate={-25}
          className="mt-1 shrink-0 w-12 sm:w-auto"
        />
        <div>
          <h1
            data-testid="planner-home-greeting"
            className="text-4xl sm:text-5xl text-[#2A2823] leading-tight"
            style={{ fontFamily: "'Playfair Display', 'Playfair Display', Georgia, serif" }}
          >
            Hi, {firstName}.
          </h1>
          <p className="mt-2 text-sm text-[#2A2823]/65">Here's what needs your attention today.</p>
        </div>
      </div>

      {/* Row 1: 60/40 — Today (+ Things I'm Carrying underneath) on the left,
          This Week on the right. 5-col grid so 3/2 lands exactly at 60/40. */}
      <div className="grid lg:grid-cols-5 gap-6 items-start">
        <div className="lg:col-span-3 space-y-6 min-w-0">
          {/* TODAY card */}
          <section
            data-testid="planner-home-today-card"
            className="bg-white rounded-2xl p-6 border border-[#A87F56]/25 shadow-[0_10px_30px_rgb(42,40,35,0.04)]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] tracking-[0.35em] text-[#A87F56]">TODAY</div>
              <Link
                to="/planner/today"
                className="text-xs text-[#3E4A37] underline underline-offset-2 hover:text-[#2f3a2a]"
                data-testid="planner-home-today-view-all"
              >
                View all
              </Link>
            </div>
            {loading ? (
              <div className="text-sm text-[#2A2823]/50">Loading…</div>
            ) : today.events.length === 0 ? (
              <div className="text-sm text-[#2A2823]/60 italic py-6 text-center">
                Nothing on the calendar yet. A quiet day is a good day.
                <div className="mt-3">
                  <Link
                    to="/planner/calendar?add=1"
                    className="text-[#3E4A37] not-italic underline underline-offset-2"
                  >
                    Plan something
                  </Link>
                </div>
              </div>
            ) : (
              <ul className="space-y-2.5" data-testid="planner-home-today-list">
                {today.events.slice(0, 6).map((ev) => (
                  <li
                    key={ev.id}
                    className="group flex items-center gap-3 py-1.5"
                    data-testid={`planner-home-today-row-${ev.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-[#EDE5DA] flex items-center justify-center shrink-0">
                      <Calendar className="w-4 h-4 text-[#3E4A37]" strokeWidth={1.6} />
                    </div>
                    <div className="text-xs tabular-nums text-[#2A2823]/70 w-16 shrink-0">
                      {ev.all_day ? "All day" : formatTime(ev.start_at)}
                    </div>
                    <div className={`text-[15px] flex-1 min-w-0 truncate ${ev.done_at ? "line-through text-[#2A2823]/40" : "text-[#2A2823]"}`}>
                      {ev.title}
                    </div>
                    <button
                      onClick={() => setEditingEvent(ev)}
                      aria-label="Edit event"
                      data-testid={`planner-home-today-edit-${ev.id}`}
                      className="text-[#2A2823]/40 hover:text-[#3E4A37] opacity-100 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 transition"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteEvent(ev)}
                      aria-label="Delete event"
                      data-testid={`planner-home-today-delete-${ev.id}`}
                      className="text-[#2A2823]/40 hover:text-[#8a3a2a] opacity-100 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleDone(ev)}
                      aria-label={ev.done_at ? "Mark not done" : "Mark done"}
                      data-testid={`planner-home-today-toggle-${ev.id}`}
                      className="text-[#3E4A37] hover:text-[#2f3a2a]"
                    >
                      {ev.done_at ? (
                        <CheckCircle2 className="w-5 h-5" strokeWidth={1.6} />
                      ) : (
                        <Circle className="w-5 h-5" strokeWidth={1.6} />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Things I'm Carrying — localStorage-backed. Sits under TODAY on
              the left column so the mental-load list reads as an extension
              of the day. */}
          <ThingsImCarrying />
        </div>

        {/* WEEK card (right column, 40%) */}
        <section
          data-testid="planner-home-week-card"
          className="lg:col-span-2 bg-white rounded-2xl p-6 border border-[#A87F56]/25 shadow-[0_10px_30px_rgb(42,40,35,0.04)]"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] tracking-[0.35em] text-[#A87F56]">THIS WEEK AT A GLANCE</div>
          </div>
          <div className="text-sm text-[#2A2823]/70">{weekLabel}</div>
          {/* Mini day strip */}
          <div className="mt-4 grid grid-cols-7 gap-1 text-center">
            {["S", "M", "T", "W", "T", "F", "S"].map((l, i) => {
              const d = addDays(weekStart, i);
              const key = ymd(d);
              const isToday = key === today.date;
              const hasEvents = (week.by_day?.[key] || []).length > 0;
              return (
                <div key={i} className="flex flex-col items-center py-1" data-testid={`planner-week-mini-${key}`}>
                  <div className="text-[10px] text-[#2A2823]/50">{l}</div>
                  <div
                    className={`mt-1 w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                      isToday
                        ? "bg-[#3E4A37] text-[#F7F3ED]"
                        : hasEvents
                          ? "bg-[#EDE5DA] text-[#2A2823]"
                          : "text-[#2A2823]/60"
                    }`}
                  >
                    {d.getDate()}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Day-by-day rundown */}
          <div className="mt-4 space-y-2 max-h-56 overflow-y-auto">
            {[...Array(7)].map((_, i) => {
              const d = addDays(weekStart, i);
              const key = ymd(d);
              const events = week.by_day?.[key] || [];
              if (events.length === 0) return null;
              return (
                <div key={key} className="flex gap-3 text-sm" data-testid={`planner-week-day-${key}`}>
                  <div className="w-10 text-[#2A2823]/60 text-xs uppercase tracking-wide pt-0.5">
                    {d.toLocaleDateString(undefined, { weekday: "short" })}
                  </div>
                  <div className="flex-1">
                    {events.slice(0, 3).map((e) => (
                      <div key={e.id} className="text-[#2A2823]">
                        <span className="text-[#2A2823]/60 mr-2">{e.all_day ? "" : formatTime(e.start_at)}</span>
                        {e.title}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3">
            <Link
              to="/planner/week"
              className="inline-flex items-center gap-1 text-xs text-[#3E4A37] underline underline-offset-2"
              data-testid="planner-home-week-view-all"
            >
              View full week <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </section>
      </div>

      {/* Row 2: Meal Plan (60%) + Quote card (40%) */}
      <div className="grid lg:grid-cols-5 gap-6">
        <section
          data-testid="planner-home-meals-card"
          className="lg:col-span-3 bg-white rounded-2xl p-6 border border-[#A87F56]/25 shadow-[0_10px_30px_rgb(42,40,35,0.04)]"
        >
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[10px] tracking-[0.35em] text-[#A87F56]">MEAL PLAN</div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <Link
                to={`/planner/shopping?week=${week.start}`}
                data-testid="planner-home-open-basket"
                className="inline-flex items-center gap-1.5 text-xs rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] px-3 h-8"
              >
                <ShoppingBasket className="w-3.5 h-3.5" /> My basket
              </Link>
              <button
                onClick={() => setCopyWeekOpen(true)}
                data-testid="planner-home-copy-week"
                className="text-xs text-[#3E4A37] underline underline-offset-2 hover:text-[#2f3a2a]"
              >
                Reuse a past week
              </button>
              <Link
                to="/plant"
                className="text-xs text-[#3E4A37] underline underline-offset-2"
                data-testid="planner-home-meals-view-recipes"
              >
                View recipes
              </Link>
            </div>
          </div>
          <div className="mb-4">
            <MealTypeTabs
              value={mealType}
              onChange={setMealType}
              counts={mealCounts}
              testidPrefix="planner-home-meal-type"
            />
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setDragging(null)}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[...Array(7)].map((_, i) => {
                const d = addDays(weekStart, i);
                const key = ymd(d);
                const entry = visibleMealEntries.find((m) => m.date === key);
                return (
                  <MealDayCell
                    key={key}
                    date={key}
                    dateObj={d}
                    entry={entry}
                    onAdd={() => setPicker({ open: true, date: key, replaceEntryId: null })}
                    onChange={() => setPicker({ open: true, date: key, replaceEntryId: entry?.id })}
                    onRemove={() => entry && removeMeal(entry.id)}
                  />
                );
              })}
            </div>
            <DragOverlay dropAnimation={null}>
              {dragging ? (
                <div className="flex items-center gap-3 py-2 px-3 bg-white rounded-xl border border-[#A87F56]/40 shadow-lg">
                  {dragging.photo ? (
                    <img
                      src={recipePhotoUrl(dragging.photo)}
                      alt=""
                      className="w-10 h-10 rounded-lg object-cover bg-[#EDE5DA]"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-[#EDE5DA] flex items-center justify-center">
                      <Utensils className="w-4 h-4 text-[#3E4A37]/60" />
                    </div>
                  )}
                  <div className="text-sm text-[#2A2823] max-w-[180px] truncate">{dragging.title}</div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
          <div className="mt-5">
            <button
              onClick={() => setPicker({ open: true, date: today.date, replaceEntryId: null })}
              data-testid="planner-home-add-meal"
              className="inline-flex items-center gap-2 rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] px-5 h-10 text-sm"
            >
              <Plus className="w-4 h-4" /> Add {mealType === "dinner" ? "a dinner" : mealType === "lunch" ? "a lunch" : "a breakfast"}
            </button>
          </div>
        </section>

        {/* Quote card */}
        <aside
          data-testid="planner-home-quote-card"
          className="lg:col-span-2 relative"
        >
          <img
            src="/quote-bg.jpg"
            alt=""
            aria-hidden="true"
            draggable={false}
            className="block w-full h-auto rounded-3xl select-none"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[#F7F3ED]/35 rounded-3xl"
          />
          <div className="absolute inset-0 flex items-center justify-center p-6 sm:p-8">
            <div className="text-center">
              <div className="text-[10px] tracking-[0.35em] text-[#A87F56]">
                JUST WHAT ACTUALLY WORKS
              </div>
              <div
                className="mt-4 italic text-xl sm:text-2xl leading-snug text-[#2A2823]"
                style={{ fontFamily: "'Playfair Display', 'Playfair Display', Georgia, serif" }}
              >
                "You don't have to<br />carry it all.<br />Just the next<br />right thing."
              </div>
              <div className="mt-4 flex items-center justify-center gap-3">
                <div className="h-px w-8 bg-[#3E4A37]/50"></div>
                <div className="text-[10px] tracking-[0.3em] text-[#3E4A37]">✦</div>
                <div className="h-px w-8 bg-[#3E4A37]/50"></div>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Row 3: Goals & Home — personal goals, up to 4 tiles */}
      <GoalsAndHome />

      {/* Inline meal picker */}
      <MealPicker
        open={picker.open}
        onClose={() => setPicker({ open: false, date: null, replaceEntryId: null })}
        date={picker.date}
        mealType={mealType}
        replaceEntryId={picker.replaceEntryId}
        onSaved={loadMeals}
      />

      <CopyWeekDialog
        open={copyWeekOpen}
        onClose={() => setCopyWeekOpen(false)}
        targetWeek={week.start}
        onCopied={loadMeals}
      />

      {/* Edit dialog for TODAY events — reused from PlannerCalendar */}
      <EventDialog
        open={!!editingEvent}
        onClose={() => setEditingEvent(null)}
        defaultDate={today.date || ymd(new Date())}
        event={editingEvent}
        onSaved={() => { setEditingEvent(null); refreshToday(); }}
      />
    </div>
  );
}

// ---------- Local widgets (localStorage-backed) ----------

const LS_CARRYING = "willoah.planner.things_carrying.v1";
const LS_GOALS = "willoah.planner.goals.v1";
const MAX_GOALS = 4;
const uid = () => Math.random().toString(36).slice(2, 10);

// ---------- Drag-to-swap meal cell ----------
function MealDayCell({ date, dateObj, entry, onAdd, onChange, onRemove }) {
  const draggable = useDraggable({ id: date, disabled: !entry });
  const droppable = useDroppable({ id: date });
  const setRefs = (node) => {
    draggable.setNodeRef(node);
    droppable.setNodeRef(node);
  };
  const dropOver = droppable.isOver && draggable.isDragging === false;
  return (
    <div
      ref={setRefs}
      className={`group flex items-center gap-3 py-2 px-2 rounded-lg transition ${
        draggable.isDragging ? "opacity-30" : ""
      } ${dropOver ? "bg-[#A87F56]/10 ring-1 ring-[#A87F56]/40" : ""}`}
      data-testid={`planner-home-meal-${date}`}
    >
      <div className="w-6 text-[10px] tracking-widest text-[#2A2823]/45 uppercase">
        {dateObj.toLocaleDateString(undefined, { weekday: "narrow" })}
      </div>

      {/* Drag handle — only when a meal exists. Cursor-grab so it's discoverable. */}
      {entry ? (
        <div
          {...draggable.listeners}
          {...draggable.attributes}
          className="shrink-0 cursor-grab active:cursor-grabbing touch-none"
          aria-label={`Drag meal for ${dateObj.toLocaleDateString()}`}
          data-testid={`planner-home-meal-drag-${date}`}
        >
          {entry.recipe?.photo_path ? (
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#EDE5DA] relative">
              <img
                src={recipePhotoUrl(entry.recipe.photo_path)}
                alt={entry.recipe.title}
                className="w-full h-full object-cover pointer-events-none"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                draggable={false}
              />
              <GripVertical
                className="absolute -left-1 top-1/2 -translate-y-1/2 w-3 h-3 text-white/80 opacity-0 group-hover:opacity-100 transition"
                strokeWidth={2}
              />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-lg bg-[#EDE5DA] flex items-center justify-center">
              <Utensils className="w-4 h-4 text-[#3E4A37]/60" strokeWidth={1.5} />
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={onAdd}
          data-testid={`planner-home-meal-slot-${date}`}
          aria-label={`Add a meal for ${dateObj.toLocaleDateString()}`}
          className="w-10 h-10 rounded-lg bg-[#EDE5DA] shrink-0 flex items-center justify-center hover:bg-[#EDE5DA]/70 border border-dashed border-transparent hover:border-[#A87F56]/40 transition"
        >
          <Utensils className="w-4 h-4 text-[#3E4A37]/60" strokeWidth={1.5} />
        </button>
      )}

      {entry ? (
        <>
          <button
            onClick={onChange}
            data-testid={`planner-home-meal-change-${date}`}
            className="flex-1 min-w-0 text-left text-sm text-[#2A2823] truncate hover:text-[#3E4A37]"
          >
            {entry.recipe?.title || <span className="italic text-[#2A2823]/45">—</span>}
          </button>
          <button
            onClick={onRemove}
            data-testid={`planner-home-meal-remove-${date}`}
            aria-label={`Remove meal for ${dateObj.toLocaleDateString()}`}
            className="text-[#2A2823]/30 hover:text-[#8a3a2a] opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </>
      ) : (
        <button
          onClick={onAdd}
          data-testid={`planner-home-meal-add-${date}`}
          className="flex-1 min-w-0 text-left text-sm italic text-[#2A2823]/45 hover:text-[#3E4A37]"
        >
          + Add a meal
        </button>
      )}
    </div>
  );
}

function useLocalList(storageKey) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(items)); } catch { /* full quota — ignore */ }
  }, [storageKey, items]);
  return [items, setItems];
}

function ThingsImCarrying() {
  const [items, setItems] = useLocalList(LS_CARRYING);
  const [draft, setDraft] = useState("");

  const add = () => {
    const t = draft.trim();
    if (!t) return;
    setItems((prev) => [...prev, { id: uid(), text: t, done: false }]);
    setDraft("");
  };
  const toggle = (id) => setItems((prev) => prev.map((it) => (it.id === id ? { ...it, done: !it.done } : it)));
  const remove = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

  return (
    <section
      data-testid="planner-home-carrying-card"
      className="bg-white rounded-2xl p-6 border border-[#A87F56]/25 shadow-[0_10px_30px_rgb(42,40,35,0.04)]"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] tracking-[0.35em] text-[#A87F56]">THINGS I'M CARRYING</div>
        <span className="text-[10px] text-[#2A2823]/40">{items.filter((i) => !i.done).length} open</span>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-[#2A2823]/60 italic mb-3">
          The little things bouncing around your head — jot them down and set them down.
        </p>
      ) : (
        <ul className="space-y-2 mb-3" data-testid="planner-home-carrying-list">
          {items.map((it) => (
            <li
              key={it.id}
              className="group flex items-center gap-3 py-1"
              data-testid={`planner-home-carrying-row-${it.id}`}
            >
              <button
                onClick={() => toggle(it.id)}
                className="text-[#3E4A37] hover:text-[#2f3a2a]"
                aria-label={it.done ? "Uncheck" : "Check"}
                data-testid={`planner-home-carrying-toggle-${it.id}`}
              >
                {it.done ? (
                  <CheckCircle2 className="w-5 h-5" strokeWidth={1.6} />
                ) : (
                  <Circle className="w-5 h-5" strokeWidth={1.6} />
                )}
              </button>
              <div
                className={`flex-1 min-w-0 text-[15px] ${
                  it.done ? "line-through text-[#2A2823]/40" : "text-[#2A2823]"
                }`}
              >
                {it.text}
              </div>
              <button
                onClick={() => remove(it.id)}
                aria-label="Remove"
                data-testid={`planner-home-carrying-remove-${it.id}`}
                className="text-[#2A2823]/30 hover:text-[#8a3a2a] opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add something you're carrying…"
          className="flex-1 rounded-lg border border-[#A87F56]/30 bg-[#F7F3ED]/60 px-3 h-10 text-sm text-[#2A2823] focus:outline-none focus:ring-1 focus:ring-[#3E4A37]"
          data-testid="planner-home-carrying-input"
          maxLength={140}
        />
        <button
          onClick={add}
          disabled={!draft.trim()}
          data-testid="planner-home-carrying-add"
          className="inline-flex items-center gap-1 rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] disabled:opacity-40 px-4 h-10 text-sm"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
    </section>
  );
}

function GoalsAndHome() {
  const [goals, setGoals] = useLocalList(LS_GOALS);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");

  const canAdd = goals.length < MAX_GOALS;

  const commitAdd = () => {
    const t = draft.trim();
    if (!t || !canAdd) { setAdding(false); setDraft(""); return; }
    setGoals((prev) => [...prev, { id: uid(), text: t }]);
    setDraft("");
    setAdding(false);
  };
  const startEdit = (g) => { setEditingId(g.id); setEditDraft(g.text); };
  const commitEdit = () => {
    const t = editDraft.trim();
    if (!t) return setEditingId(null);
    setGoals((prev) => prev.map((g) => (g.id === editingId ? { ...g, text: t } : g)));
    setEditingId(null);
  };
  const remove = (id) => {
    setGoals((prev) => prev.filter((g) => g.id !== id));
    if (editingId === id) setEditingId(null);
  };

  return (
    <section
      data-testid="planner-home-goals-card"
      className="bg-white rounded-2xl p-6 border border-[#A87F56]/25 shadow-[0_10px_30px_rgb(42,40,35,0.04)]"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] tracking-[0.35em] text-[#A87F56]">GOALS &amp; HOME</div>
        <span className="text-[10px] text-[#2A2823]/40">{goals.length}/{MAX_GOALS}</span>
      </div>

      {goals.length === 0 && !adding ? (
        <div className="py-6 text-center">
          <p className="text-sm text-[#2A2823]/60 italic mb-3">
            What are you tending this season? Add up to four goals to keep front-of-mind.
          </p>
          <button
            onClick={() => setAdding(true)}
            data-testid="planner-home-goals-add-first"
            className="inline-flex items-center gap-1 rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] px-5 h-10 text-sm"
          >
            <Plus className="w-4 h-4" /> Add your first goal
          </button>
        </div>
      ) : (
        <div
          className={`grid gap-3 ${
            goals.length + (adding ? 1 : 0) >= 3
              ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
              : "grid-cols-1 sm:grid-cols-2"
          }`}
          data-testid="planner-home-goals-grid"
        >
          {goals.map((g) => (
            <div
              key={g.id}
              className="group relative rounded-xl border border-[#A87F56]/30 bg-[#F7F3ED]/60 p-4 min-h-[96px] flex flex-col justify-between"
              data-testid={`planner-home-goals-tile-${g.id}`}
            >
              {editingId === g.id ? (
                <>
                  <textarea
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commitEdit(); }
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    autoFocus
                    rows={2}
                    className="w-full bg-transparent text-[15px] text-[#2A2823] focus:outline-none resize-none"
                    maxLength={140}
                    data-testid={`planner-home-goals-edit-input-${g.id}`}
                  />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button
                      onClick={() => setEditingId(null)}
                      className="text-xs text-[#2A2823]/60 hover:text-[#2A2823]"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={commitEdit}
                      data-testid={`planner-home-goals-edit-save-${g.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] px-3 h-8 text-xs"
                    >
                      <Check className="w-3.5 h-3.5" /> Save
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[15px] text-[#2A2823] leading-snug break-words">
                    {g.text}
                  </div>
                  <div className="flex items-center justify-end gap-2 mt-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                    <button
                      onClick={() => startEdit(g)}
                      aria-label="Edit goal"
                      data-testid={`planner-home-goals-edit-${g.id}`}
                      className="text-[#2A2823]/50 hover:text-[#3E4A37]"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => remove(g.id)}
                      aria-label="Delete goal"
                      data-testid={`planner-home-goals-remove-${g.id}`}
                      className="text-[#2A2823]/50 hover:text-[#8a3a2a]"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}

          {/* Inline add tile */}
          {adding && canAdd && (
            <div
              className="rounded-xl border border-dashed border-[#A87F56]/50 bg-[#F7F3ED]/60 p-4 min-h-[96px] flex flex-col justify-between"
              data-testid="planner-home-goals-add-tile"
            >
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commitAdd(); }
                  if (e.key === "Escape") { setAdding(false); setDraft(""); }
                }}
                autoFocus
                rows={2}
                placeholder="Your goal…"
                className="w-full bg-transparent text-[15px] text-[#2A2823] focus:outline-none resize-none placeholder:text-[#2A2823]/35"
                maxLength={140}
                data-testid="planner-home-goals-add-input"
              />
              <div className="flex items-center justify-end gap-2 mt-2">
                <button
                  onClick={() => { setAdding(false); setDraft(""); }}
                  className="text-xs text-[#2A2823]/60 hover:text-[#2A2823]"
                >
                  Cancel
                </button>
                <button
                  onClick={commitAdd}
                  disabled={!draft.trim()}
                  data-testid="planner-home-goals-add-save"
                  className="inline-flex items-center gap-1 rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] disabled:opacity-40 px-3 h-8 text-xs"
                >
                  <Check className="w-3.5 h-3.5" /> Add
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add-another button (only if not currently adding & under cap & has goals) */}
      {goals.length > 0 && !adding && canAdd && (
        <div className="mt-4">
          <button
            onClick={() => setAdding(true)}
            data-testid="planner-home-goals-add-more"
            className="inline-flex items-center gap-1 text-xs text-[#3E4A37] hover:text-[#2f3a2a] underline underline-offset-2"
          >
            <Plus className="w-3.5 h-3.5" /> Add another goal
          </button>
        </div>
      )}
    </section>
  );
}
